import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Code2,
  Video,
  MessageSquareText,
  CalendarClock,
  Users,
  ShieldCheck,
  Globe,
  Zap,
  Quote,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import SectionHeading from "../Components/ui/SectionHeading";
import Avatar from "../Components/ui/Avatar";
import Footer from "../Components/Footer";

const features = [
  {
    icon: Code2,
    title: "Live Coding",
    description:
      "Collaborate on code in real-time with syntax highlighting and instant execution for technical interviews.",
  },
  {
    icon: Video,
    title: "Video & Audio",
    description:
      "High-quality video and audio for a seamless interview experience, no extra software needed.",
  },
  {
    icon: MessageSquareText,
    title: "Instant Feedback",
    description:
      "Share feedback instantly and keep track of candidate performance with built-in evaluation tools.",
  },
  {
    icon: CalendarClock,
    title: "Easy Scheduling",
    description:
      "Schedule interviews, send invites, and manage your calendar all in one place.",
  },
];

const reasons = [
  {
    icon: Users,
    title: "Real-Time Collaboration",
    description:
      "Experience seamless real-time collaboration with candidates. Share code, whiteboard ideas, and communicate effectively during technical interviews.",
  },
  {
    icon: ShieldCheck,
    title: "Secure & Reliable",
    description:
      "Your data is protected with enterprise-grade security. Conduct interviews with confidence knowing your information is safe and secure.",
  },
  {
    icon: Globe,
    title: "Multi-Language Support",
    description:
      "Support for multiple programming languages including JavaScript, Python, Java, C++, and more. Test candidates in their preferred language.",
  },
  {
    icon: Zap,
    title: "No Setup Required",
    description:
      "Get started instantly without any downloads or installations. Everything runs in your browser for maximum convenience.",
  },
];

const steps = [
  {
    step: "01",
    title: "Create Account",
    description:
      "Sign up for free and set up your interviewer profile in minutes. No credit card required.",
  },
  {
    step: "02",
    title: "Schedule Interview",
    description:
      "Create an interview session, customize your questions, and send invites to candidates.",
  },
  {
    step: "03",
    title: "Start Interviewing",
    description:
      "Conduct the interview with our powerful tools. Collaborate, evaluate, and make decisions faster.",
  },
];

const testimonials = [
  {
    quote:
      "This platform has revolutionized our hiring process. We've reduced our time-to-hire by 40% and improved candidate experience significantly.",
    name: "Sarah Johnson",
    role: "Head of Talent, Tech Corp",
  },
  {
    quote:
      "The real-time coding feature is incredible. We can properly assess technical skills and see how candidates think through problems.",
    name: "Michael Chen",
    role: "Engineering Manager, StartupXYZ",
  },
  {
    quote:
      "Easy to use, reliable, and packed with features. Our entire recruitment team loves it. Highly recommended!",
    name: "Emily Rodriguez",
    role: "Talent Acquisition Lead, Global Solutions",
  },
];

export default function Home() {
  const navigate = useNavigate();
  const { authenticated, user } = useSelector((state) => state.auth);

  useEffect(() => {
    document.title = "Interviewly - Home";
  }, []);

  const handleGetStarted = () => {
    if (authenticated && user) {
      navigate("/dashboard");
    } else {
      navigate("/signup");
    }
  };

  return (
    <div className="min-h-screen bg-cocoa-800">
      {/* Hero */}
      <section className="relative overflow-hidden px-4 pb-20 pt-24 text-center sm:pb-28 sm:pt-32">
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-96 bg-[radial-gradient(ellipse_at_top,rgba(251,191,36,0.12),transparent_60%)]"
          aria-hidden="true"
        />
        <div className="container-page relative">
          <div className="mx-auto mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-sm font-medium text-amber-200">
            <Sparkles className="h-4 w-4 text-amber-400" />
            Real-time interview platform for modern hiring teams
          </div>
          <h1 className="mx-auto max-w-4xl text-4xl font-bold tracking-tight text-cream-100 sm:text-5xl lg:text-6xl">
            Interviewly – Real-Time
            <span className="text-amber-400"> Interview Platform</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-amber-100/75 sm:text-xl">
            Conduct, schedule, and manage interviews seamlessly with real-time
            video, code collaboration, and instant feedback. Empower your hiring
            process with modern tools.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <button onClick={handleGetStarted} className="btn btn-primary btn-lg w-full sm:w-auto">
              Get Started
              <ArrowRight className="h-4 w-4" />
            </button>
            <button
              onClick={() => navigate("/login")}
              className="btn btn-secondary btn-lg w-full sm:w-auto"
            >
              Sign in
            </button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="px-4 pb-20 sm:pb-28">
        <div className="container-page">
          <SectionHeading
            eyebrow="Everything included"
            title="Tools for every stage of the interview"
            subtitle="Four core capabilities, one seamless experience. Everything your team needs runs right in the browser."
          />
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <div
                  key={feature.title}
                  className="card card-hover group flex flex-col p-7"
                >
                  <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-amber-400/10 text-amber-300 ring-1 ring-inset ring-amber-400/20 transition-transform duration-200 group-hover:scale-105">
                    <Icon className="h-6 w-6" strokeWidth={2} />
                  </div>
                  <h3 className="text-xl font-semibold tracking-tight text-cream-100">
                    {feature.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-amber-100/60">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="bg-cocoa-900 px-4 py-20 sm:py-28">
        <div className="container-page">
          <SectionHeading
            eyebrow="Why Interviewly"
            title="Why choose our platform?"
            subtitle="Built for reliability and speed, so your team can focus on finding the right people."
          />
          <div className="grid gap-x-16 gap-y-10 md:grid-cols-2">
            {reasons.map((reason) => {
              const Icon = reason.icon;
              return (
                <div key={reason.title} className="flex items-start gap-5">
                  <div className="mt-0.5 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-400/10 text-amber-300 ring-1 ring-inset ring-amber-400/20">
                    <Icon className="h-5 w-5" strokeWidth={2} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold tracking-tight text-cream-100">
                      {reason.title}
                    </h3>
                    <p className="mt-2 leading-relaxed text-amber-100/60">
                      {reason.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="px-4 py-20 sm:py-28">
        <div className="container-page">
          <SectionHeading
            eyebrow="How it works"
            title="Up and running in three steps"
            subtitle="Getting started is simple. Follow these three easy steps to begin conducting professional interviews."
          />
          <div className="grid gap-6 md:grid-cols-3">
            {steps.map((item, index) => (
              <div key={item.step} className="relative">
                <div className="card h-full p-8">
                  <div className="mb-6 inline-flex h-12 w-12 items-center justify-center rounded-full bg-amber-400 text-lg font-bold text-cocoa-950 shadow-gold-inset">
                    {index + 1}
                  </div>
                  <h3 className="text-xl font-semibold tracking-tight text-cream-100">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-amber-100/60">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-cocoa-900 px-4 py-20 sm:py-28">
        <div className="container-page">
          <SectionHeading
            eyebrow="Testimonials"
            title="Trusted by leading companies"
            subtitle="Hiring teams around the world run their interviews on Interviewly."
          />
          <div className="grid gap-6 md:grid-cols-3">
            {testimonials.map((t) => (
              <div key={t.name} className="card flex flex-col p-8">
                <Quote
                  className="mb-5 h-8 w-8 text-amber-400/40"
                  strokeWidth={1.75}
                />
                <p className="flex-1 leading-relaxed text-amber-100/80">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <div className="mt-8 flex items-center gap-3 border-t border-white/10 pt-6">
                  <Avatar name={t.name} size="md" />
                  <div>
                    <p className="text-sm font-semibold text-cream-100">{t.name}</p>
                    <p className="text-xs text-amber-100/50">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 py-20 sm:py-28">
        <div className="container-page">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-amber-400 to-amber-500 px-6 py-16 text-center shadow-modal sm:px-12 sm:py-20">
            <div
              className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.35),transparent_50%)]"
              aria-hidden="true"
            />
            <div className="relative">
              <h2 className="mx-auto max-w-2xl text-3xl font-bold tracking-tight text-cocoa-950 sm:text-4xl">
                Ready to transform your hiring process?
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-lg text-cocoa-900/80">
                Join thousands of companies already using our platform to find
                the best talent.
              </p>
              <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <Link
                  to="/dashboard"
                  className="btn btn-dark btn-lg w-full sm:w-auto"
                >
                  Start Free Trial
                </Link>
                <Link
                  to="/dashboard"
                  className="btn btn-lg w-full border-2 border-cocoa-950/20 bg-transparent text-cocoa-950 hover:bg-cocoa-950/10 sm:w-auto"
                >
                  Schedule Demo
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
